import React from "react";
import { Column } from "../../common/Table/Interfaces";
import { TABLE_COLUMNS } from "../../../constants/translates";
import { Declarant } from "../../../models/Declarant";
import {
  DECLARANT_MISTAKES,
  EXPOSED_VALUES,
  NO_KEY,
  YES_KEY,
} from "../../../constants/values";
import DeclarantActions from "../DeclarantActions";
import TextArea from "../declarantTable/columns/text";
import { CLIENT_ID } from "../../../constants/table";
import {
  ALL_FIELDS,
  DELETE_DECLARANT,
  EDIT_DECLARANT,
} from "../../../constants/authorize";
import Department from "../declarantTable/columns/department";
import SearchFilter from "../declarantTable/filters/searchFilter";
import Mistakes from "../declarantTable/columns/mistakes";
import { IObjectMap } from "../../../interfaces/common";
import Exposed from "../declarantTable/columns/exposed";
import {
  CHANGE_TABLE_CELL_EVENT,
  DELETE_CREATED_EVENT,
} from "../../../constants/events";
import ClientFilter from "../declarantTable/filters/selectColumns/client";
import CreatorFilter from "../declarantTable/filters/selectColumns/creator";
import DepartmentFilter from "../declarantTable/filters/selectColumns/department";
import MistakesFilter from "../declarantTable/filters/selectColumns/mistakes";
import ExposedFilter from "../declarantTable/filters/selectColumns/exposed";
import Client from "../columns/client";
import dateTimeFilter from "../declarantTable/filters/dateTimeFilter";
import parseDate from "../../../helpers/date";
import SettingsStore from "../../../stores/SettingsStore";

const COLUMNS = (settingsStore: SettingsStore) => [
  {
    title: TABLE_COLUMNS.INDEX,
    dataIndex: "row_number",
    key: "row_number",
    sorter: true,
    isActive: true,
    order: 1,
    width: 40,
    render: (text, entity: Declarant) => entity.row_number,
  },
  {
    title: TABLE_COLUMNS.epi,
    dataIndex: "epi",
    key: "epi",
    sorter: true,
    order: 1,
    width: 160,
    ellipsis: true,
    render: (text, entity: Declarant) => (
      <TextArea value={text} entityKey="epi" entityId={entity.id} />
    ),
    filterDropdown: SearchFilter,
    visible: false,
    isActive: true,
  },
  {
    title: TABLE_COLUMNS.client,
    dataIndex: "client_id",
    key: "client_id",
    sorter: true,
    order: 2,
    width: 150,
    ellipsis: true,
    render: (text, entity: Declarant) => (
      <Client
        option={(entity as Declarant).client?.nickname || ""}
        entityKey={CLIENT_ID}
        entityId={(entity as Declarant).id}
        eventKey={CHANGE_TABLE_CELL_EVENT}
        permission={EDIT_DECLARANT}
      />
    ),
    filterDropdown: ClientFilter,
    isActive: true,
  },
  {
    title: TABLE_COLUMNS.department,
    dataIndex: "department_id",
    key: "department_id",
    sorter: true,
    order: 2,
    width: 150,
    ellipsis: true,
    render: (text, entity: Declarant) => <Department entity={entity} />,
    onlyOnPermission: [ALL_FIELDS],
    filterDropdown: DepartmentFilter,
    isActive: true,
  },
  {
    title: TABLE_COLUMNS.creator,
    dataIndex: "creator",
    key: "creator",
    sorter: true,
    isActive: true,
    order: 2,
    width: 140,
    ellipsis: true,
    render: (text, entity: Declarant): string | null =>
      entity.user?.nickname || null,
    filterDropdown: CreatorFilter,
    onlyOnPermission: [ALL_FIELDS],
  },
  {
    title: TABLE_COLUMNS.means_of_transportation,
    dataIndex: "means_of_transportation",
    key: "means_of_transportation",
    sorter: true,
    order: 3,
    width: 135,
    ellipsis: true,
    filterDropdown: SearchFilter,
    isActive: true,
  },
  {
    title: TABLE_COLUMNS.carrier,
    dataIndex: "carrier",
    key: "carrier",
    sorter: true,
    order: 4,
    width: 150,
    ellipsis: true,
    filterDropdown: SearchFilter,
    isActive: true,
  },
  {
    title: TABLE_COLUMNS.driver,
    dataIndex: "driver",
    key: "driver",
    sorter: true,
    order: 5,
    width: 130,
    ellipsis: true,
    filterDropdown: SearchFilter,
    isActive: true,
  },
  {
    title: TABLE_COLUMNS.consignor,
    dataIndex: "consignor",
    key: "consignor",
    sorter: true,
    order: 6,
    width: 130,
    ellipsis: true,
    filterDropdown: SearchFilter,
    isActive: true,
  },
  {
    title: TABLE_COLUMNS.consignment_items,
    dataIndex: "consignment_items",
    key: "consignment_items",
    sorter: true,
    order: 7,
    width: 60,
    ellipsis: true,
    filterDropdown: SearchFilter,
    isActive: true,
  },
  {
    title: TABLE_COLUMNS.invoice,
    dataIndex: "invoice",
    key: "invoice",
    sorter: true,
    order: 8,
    width: 80,
    ellipsis: true,
    filterDropdown: SearchFilter,
    isActive: true,
  },
  {
    title: TABLE_COLUMNS.currency,
    dataIndex: "currency",
    key: "currency",
    sorter: true,
    order: 9,
    width: 40,
    ellipsis: true,
    filterDropdown: SearchFilter,
    isActive: true,
  },
  {
    title: TABLE_COLUMNS.cargo_quantity,
    dataIndex: "cargo_quantity",
    key: "cargo_quantity",
    sorter: true,
    order: 10,
    width: 55,
    ellipsis: true,
    filterDropdown: SearchFilter,
    isActive: true,
  },
  {
    title: TABLE_COLUMNS.brutto,
    dataIndex: "brutto",
    key: "brutto",
    sorter: true,
    order: 11,
    width: 75,
    ellipsis: true,
    filterDropdown: SearchFilter,
    isActive: true,
  },
  {
    title: TABLE_COLUMNS.cmr,
    dataIndex: "cmr",
    key: "cmr",
    sorter: true,
    order: 12,
    width: 90,
    ellipsis: true,
    filterDropdown: SearchFilter,
    isActive: true,
  },
  {
    title: TABLE_COLUMNS.date_cmr,
    dataIndex: "date_cmr",
    key: "date_cmr",
    sorter: true,
    order: 13,
    width: 80,
    ellipsis: true,
    render: (text, entity: Declarant) => parseDate(entity.date_cmr),
    filterDropdown: dateTimeFilter,
    isActive: true,
  },
  {
    title: TABLE_COLUMNS.border_checkpoint_name,
    dataIndex: "border_checkpoint_name",
    key: "border_checkpoint_name",
    sorter: true,
    order: 14,
    width: 110,
    ellipsis: true,
    filterDropdown: SearchFilter,
    isActive: true,
  },
  {
    title: TABLE_COLUMNS.internal_note,
    dataIndex: "internal_note",
    key: "internal_note",
    sorter: true,
    order: 15,
    width: 80,
    ellipsis: true,
    render: (text, entity: Declarant) => (
      <TextArea value={text} entityKey="internal_note" entityId={entity.id} />
    ),
    filterDropdown: SearchFilter,
    isActive: true,
    onlyOnPermission: [ALL_FIELDS],
  },
  {
    title: TABLE_COLUMNS.note,
    dataIndex: "note",
    key: "note",
    sorter: true,
    order: 16,
    width: 80,
    ellipsis: true,
    render: (text, entity: Declarant) => (
      <TextArea value={text} entityKey="note" entityId={entity.id} />
    ),
    filterDropdown: SearchFilter,
    isActive: true,
  },
  {
    title: TABLE_COLUMNS.mistakes,
    dataIndex: "mistakes",
    key: "mistakes",
    sorter: true,
    order: 17,
    width: 140,
    ellipsis: true,
    render: (text: keyof IObjectMap, entity: Declarant) => (
      <Mistakes
        option={text}
        entityKey="mistakes"
        entityId={entity.id}
        options={DECLARANT_MISTAKES}
      />
    ),
    onlyOnPermission: [ALL_FIELDS],
    filterDropdown: MistakesFilter,
    isActive: true,
  },
  {
    title: TABLE_COLUMNS.measure_code,
    dataIndex: "measure_code",
    key: "measure_code",
    sorter: true,
    order: 19,
    width: 70,
    ellipsis: true,
    filterDropdown: SearchFilter,
    isActive: true,
  },
  {
    title: TABLE_COLUMNS.measure_code_doc_number,
    dataIndex: "measure_code_doc_number",
    key: "measure_code_doc_number",
    sorter: true,
    order: 20,
    width: 160,
    ellipsis: true,
    filterDropdown: SearchFilter,
    isActive: true,
  },
  {
    title: TABLE_COLUMNS.warranty,
    dataIndex: "warranty",
    key: "warranty",
    sorter: true,
    order: 21,
    width: 50,
    ellipsis: true,
    filterDropdown: SearchFilter,
    isActive: true,
  },
  {
    title: TABLE_COLUMNS.way,
    dataIndex: "way",
    key: "way",
    sorter: true,
    order: 22,
    width: 60,
    ellipsis: true,
    filterDropdown: SearchFilter,
    isActive: true,
  },
  {
    title: TABLE_COLUMNS.customs_code_and_office_name,
    dataIndex: "customs_code_and_office_name",
    key: "customs_code_and_office_name",
    sorter: true,
    order: 24,
    width: 180,
    ellipsis: true,
    filterDropdown: SearchFilter,
    isActive: true,
  },
  {
    title: TABLE_COLUMNS.exposed,
    dataIndex: "exposed",
    key: "exposed",
    sorter: true,
    order: 25,
    width: 80,
    ellipsis: true,
    render: (text, entity: Declarant) => (
      <Exposed
        option={text ? YES_KEY : NO_KEY}
        entityKey="exposed"
        entityId={entity.id}
        options={EXPOSED_VALUES}
      />
    ),
    filterDropdown: ExposedFilter,
    isActive: true,
  },
  {
    title: TABLE_COLUMNS.actions,
    dataIndex: "actions",
    key: "actions",
    sorter: false,
    order: 9999,
    width: 120,
    ellipsis: true,
    render: (text, entity: Declarant) => {
      return (
        <DeclarantActions
          editEntity={{
            entity,
          }}
          deleteEntity={{ entityId: entity.id, eventKey: DELETE_CREATED_EVENT }}
          settingsStore={settingsStore}
        />
      );
    },
    onlyOnPermission: [DELETE_DECLARANT],
    isActive: true,
  },
  {
    title: TABLE_COLUMNS.created_at,
    dataIndex: "creation_date",
    key: "creation_date",
    sorter: true,
    isActive: true,
    order: 3,
    width: 80,
    ellipsis: true,
    render: (text, entity: Declarant): string | null =>
      parseDate(entity.creation_date),
    filterDropdown: dateTimeFilter,
  },
] as Column[];

export default COLUMNS;
